import React, { useCallback, useMemo } from 'react';
import { Button, Tooltip } from 'antd';
import { getUserDetails } from '../../../../utils/helper';
import type { ReportStatus } from '../../types';

interface StatusActionsProps {
  statusList: ReportStatus[];
  statusLoading: boolean;
  /** Effective order state derived from committed reports: SCANNED | DRAFTED | REVIEWED | SIGNEDOFF */
  orderReportState: string;
  /** Status code of the action currently in flight — shows a spinner on that button
   *  and locks the others until it settles */
  pendingAction?: string | null;
  proxyMode?: 'draft' | 'signoff' | null;
  /** Hard-disable every action while the editor is locked (e.g. a signature preview is
   *  rebuilding + reopening the document). */
  locked?: boolean;
  /** The user's effective reporting level for this study (ordinal 0..4), from
   *  useReportingLevel. When `enforced` is true these gate the buttons; when false
   *  or null, legacy role-based gating applies. */
  maxLevel?: number | null;
  enforced?: boolean;
  /** True when the identity that would be stamped on this sign-off (the proxy signer if
   *  one is selected, otherwise the logged-in user) has no signature on file. Sign Off is
   *  blocked: the report would be finalized carrying no signature at all. */
  signatureMissing?: boolean;
  onAction: (statusCode: string) => void;
}

// Ordinal reporting levels (mirror of reporting-privilege-helper on the backend).
const LEVEL = { NONE: 0, VIEW: 1, DRAFT: 2, REVIEWED: 3, SIGNOFF: 4 };
// The level each committed status requires.
const STATUS_MIN_LEVEL: Record<string, number> = {
  DRAFTED: LEVEL.DRAFT,
  REVIEWED: LEVEL.REVIEWED,
  SIGNEDOFF: LEVEL.SIGNOFF,
};

const SIGNOFF_ICON = (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0.199951 2.2001C0.199951 1.3176 0.917451 0.600098 1.79995 0.600098H8.19995C9.08245 0.600098 9.79995 1.3176 9.79995 2.2001V12.6276C9.79995 13.2676 9.08745 13.6476 8.55495 13.2926L4.99995 10.9201L1.44495 13.2926C0.912451 13.6476 0.199951 13.2651 0.199951 12.6276V2.2001ZM1.79995 1.8001C1.57995 1.8001 1.39995 1.9801 1.39995 2.2001V11.8801L4.33495 9.9251C4.73745 9.6576 5.26245 9.6576 5.66495 9.9251L8.59995 11.8801V2.2001C8.59995 1.9801 8.41995 1.8001 8.19995 1.8001H1.79995Z" fill="white"/>
  </svg>
);

// Non-final statuses. Once signed off they stay visible but disabled — only Sign off remains
// actionable (re-reporting always produces a new signed entry).
const DRAFT_LIKE = ['DRAFTED', 'REVIEWED'];

const getButtonProps = (statusCode: string) => {
  switch (statusCode) {
    case 'SIGNEDOFF':
      return { type: 'primary' as const, className: 'footer-btn-signoff' };
    default:
      return { type: 'default' as const, className: 'footer-btn' };
  }
};

const StatusActions: React.FC<StatusActionsProps> = ({
  statusList,
  statusLoading,
  orderReportState,
  pendingAction = null,
  proxyMode = null,
  locked = false,
  maxLevel = null,
  enforced = false,
  signatureMissing = false,
  onAction,
}) => {
  const userType = getUserDetails()?.user_type;

  // Below the level a status requires? Only applied when the backend is actually
  // enforcing (enforced=true) and we have a resolved level — otherwise the UI
  // could hide a button the server would still accept. When not enforcing, fall
  // back to the legacy role gate (only Sign-off is role-restricted).
  const belowLevel = useCallback((statusCode: string) => {
    if (statusCode === 'SIGNEDOFF' && proxyMode === 'signoff') return false;
    if (!enforced || maxLevel == null) return false;
    const min = STATUS_MIN_LEVEL[statusCode];
    return min != null && maxLevel < min;
  }, [enforced, maxLevel, proxyMode]);

  // The workflow only moves forward: SCANNED → DRAFTED → REVIEWED → SIGNEDOFF.
  // Draft locks once the order is REVIEWED; both lock once SIGNEDOFF.
  const getDisabledState = useCallback((statusCode: string) => {
    if (belowLevel(statusCode)) return true;
    switch (statusCode) {
      case 'DRAFTED':
        return ['REVIEWED', 'SIGNEDOFF'].includes(orderReportState);
      case 'REVIEWED':
        return orderReportState === 'SIGNEDOFF';
      case 'SIGNEDOFF':
        // No signature on file → the signed report would carry none, so sign-off is
        // blocked regardless of role or privilege level. Drafting/reviewing stay open.
        if (signatureMissing) return true;
        // When enforcing, the level check above is the gate; otherwise keep the
        // legacy role restriction (only radiologist,hod and typist may sign off).
        return enforced ? false : !['hod', 'radiologist','typist'].includes(userType);
      default:
        return false;
    }
  }, [orderReportState, userType, belowLevel, enforced, signatureMissing]);

  const buttons = useMemo(() => {
    const fallback: ReportStatus[] = [
      { status_id: 1, status_code: 'DRAFTED', status_label: 'Draft', status_order: 1 },
      { status_id: 2, status_code: 'REVIEWED', status_label: 'Reviewed', status_order: 2 },
      { status_id: 3, status_code: 'SIGNEDOFF', status_label: 'Sign Off', status_order: 3 },
    ];

    const list = statusLoading || !statusList.length ? fallback : statusList;
    const filtered = list
      .filter(s => !(proxyMode === 'signoff' && DRAFT_LIKE.includes(s.status_code)))
      .filter(s => !(proxyMode === 'draft' && s.status_code === 'SIGNEDOFF'));

    return filtered.map(status => {
      const props = getButtonProps(status.status_code);
      const isPending = pendingAction === status.status_code;
      // An antd Button that is disabled swallows pointer events, so the reason has to be
      // carried on a wrapper for the tooltip to ever show.
      const blockedForSignature = status.status_code === 'SIGNEDOFF' && signatureMissing;
      const button = (
        <Button
          key={status.status_id}
          className={props.className}
          type={props.type}
          loading={isPending}
          disabled={locked || getDisabledState(status.status_code) || (!!pendingAction && !isPending)}
          onClick={() => onAction(status.status_code)}
        >
          {status.status_code === 'SIGNEDOFF' && !isPending && SIGNOFF_ICON}
          {status.status_label}
        </Button>
      );
      if (!blockedForSignature) return button;
      return (
        <Tooltip
          key={status.status_id}
          title="No signature on file for the signing doctor — the report would be signed off blank. Ask an administrator to add a signature to the user profile."
        >
          <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>{button}</span>
        </Tooltip>
      );
    });
  }, [statusList, statusLoading, proxyMode, pendingAction, locked, getDisabledState, onAction, signatureMissing]);

  return <>{buttons}</>;
};

export default StatusActions;
